// Вкладка «Лайфхаки»: очередь на проверку и решения по ней.
//
// Это единственное место, где видно неопубликованное. Устроено как очередь, а
// не как таблица со всеми записями: работа модератора — разобрать то, что
// пришло, а не листать архив. Поэтому по умолчанию открывается `pending`, а
// счётчик на переключателе показывает, сколько ещё ждёт.
//
// Решение применяется сразу, без «Сохранить»: строк много, каждая независима,
// и общая кнопка означала бы, что случайное закрытие вкладки теряет полчаса
// работы. Обратная сторона — опубликованное можно вернуть в очередь тем же
// переключателем статуса, ошибка модератора обратима.
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useI18n, useT } from '../i18n';
import { errorKey } from '../auth';
import type { ApiError } from '../auth';
import {
  countryNamer,
  deleteAdminLifehack,
  fetchAdminLifehacks,
  setAdminStatus,
  type Lifehack,
  type LifehackStatus,
} from '../lifehacks';
import styles from './LifehacksPanel.module.css';

const STATUSES: LifehackStatus[] = ['pending', 'published', 'rejected'];

export function LifehacksPanel(): ReactElement {
  const t = useT();
  const { lang } = useI18n();

  const [status, setStatus] = useState<LifehackStatus>('pending');
  const [items, setItems] = useState<Lifehack[]>([]);
  const [counts, setCounts] = useState<Record<LifehackStatus, number>>({
    pending: 0,
    published: 0,
    rejected: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  /** id строки, по которой сейчас идёт запрос: блокируется она, а не вся
   *  очередь — соседние решения принимать это не мешает. */
  const [busy, setBusy] = useState<string>('');

  // Язык интерфейса модератора едет в запрос: решение принимается по тексту, и
  // читать очередь он должен на своём языке. Смена языка перечитывает список —
  // иначе на экране остались бы тексты на прежнем.
  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchAdminLifehacks(status, lang);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setError(null);
    setItems(res.value.items);
    setCounts(res.value.counts);
  }, [status, lang]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(item: Lifehack, next: LifehackStatus): Promise<void> {
    setBusy(item.id);
    const res = await setAdminStatus(item.id, next, lang);
    setBusy('');
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setError(null);
    // Строка уезжает из текущего списка: она больше не в том статусе, который
    // открыт. Перезапрашивать весь список ради этого незачем — только счётчики.
    setItems((prev) => prev.filter((row) => row.id !== item.id));
    setCounts((prev) => ({
      ...prev,
      [item.status]: Math.max(0, prev[item.status] - 1),
      [next]: prev[next] + 1,
    }));
  }

  async function remove(item: Lifehack): Promise<void> {
    setBusy(item.id);
    const res = await deleteAdminLifehack(item.id);
    setBusy('');
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setError(null);
    setItems((prev) => prev.filter((row) => row.id !== item.id));
    setCounts((prev) => ({ ...prev, [item.status]: Math.max(0, prev[item.status] - 1) }));
  }

  const countryName = useMemo(() => countryNamer(lang), [lang]);
  const date = useMemo(
    () => new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }),
    [lang],
  );

  return (
    <div className={styles.panel}>
      <div className={styles.bar} role="group" aria-label={t('admin.lh.queue')}>
        {STATUSES.map((key) => (
          <button
            key={key}
            type="button"
            className={`${styles.tab} ${status === key ? styles.tabOn : ''}`}
            aria-pressed={status === key}
            onClick={() => setStatus(key)}
          >
            {t(`lh.status.${key}`)}
            <span className={styles.count}>{counts[key]}</span>
          </button>
        ))}
      </div>

      {error && <p className={styles.error}>{t(errorKey(error))}</p>}

      {loading ? (
        <p className={styles.muted}>{t('lh.loading')}</p>
      ) : items.length === 0 ? (
        <p className={styles.muted}>{t('admin.lh.empty')}</p>
      ) : (
        <div className={styles.list}>
          {items.map((item) => (
            <article key={item.id} className={styles.row}>
              <p className={styles.text}>{item.text}</p>

              <div className={styles.meta}>
                {item.category && <span className={styles.tag}>{t(`lh.cat.${item.category}`)}</span>}
                {item.country && <span className={styles.tag}>{countryName(item.country)}</span>}
                {/* Редакционную запись модератор обязан отличать от
                    пользовательской: решения по ним разные — чужой совет он
                    проверяет, свой (наш) сюда попал из сида и правится в коде. */}
                {item.source === 'editorial' ? (
                  <span className={styles.tag}>{t('lh.editorial')}</span>
                ) : (
                  <span className={styles.author}>
                    {item.authorName.trim() || t('lh.author.anon')}
                  </span>
                )}
                <span className={styles.date}>{date.format(item.createdAt)}</span>
                {item.confirms > 0 && (
                  <span className={styles.tag}>{`✓ ${item.confirms}`}</span>
                )}
              </div>

              <div className={styles.actions}>
                {status !== 'published' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy === item.id}
                    onClick={() => void decide(item, 'published')}
                  >
                    {t('admin.lh.publish')}
                  </button>
                )}
                {status !== 'rejected' && (
                  <button
                    type="button"
                    className="btn"
                    disabled={busy === item.id}
                    onClick={() => void decide(item, 'rejected')}
                  >
                    {t('admin.lh.reject')}
                  </button>
                )}
                {status !== 'pending' && (
                  <button
                    type="button"
                    className="btn"
                    disabled={busy === item.id}
                    onClick={() => void decide(item, 'pending')}
                  >
                    {t('admin.lh.requeue')}
                  </button>
                )}
                <span className={styles.spacer} />
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={busy === item.id}
                  onClick={() => void remove(item)}
                >
                  {t('admin.lh.delete')}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
